// src/web/client/src/pages/Layout.jsx
import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { getShopDomain } from '@/utils/shopContext';
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, Zap, History, BarChart3, Settings, CreditCard, HelpCircle } from 'lucide-react';
import DemoModeBanner from '@/components/DemoModeBanner';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useSubscriptionStatus } from '@/components/subscription/SubscriptionGuard';

// The menu (UX brief, Phase B): six items, no section headings, Billing and
// Help at the bottom. Every item is visible on every plan — what a plan does
// not include is locked INSIDE the page (the pattern the app already had for
// Analytics / Pro Tools), never hidden from the menu.
const navigation = [
  { name: 'Home', href: '/home', icon: LayoutDashboard },
  { name: 'Customers', href: '/customers', icon: Users, matchPrefix: true },
  { name: 'Automations', href: '/automations', icon: Zap },
  { name: 'Activity', href: '/activity', icon: History },
  { name: 'Results', href: '/results', icon: BarChart3 },
  { name: 'Settings', href: '/settings', icon: Settings },
];
const footerNavigation = [
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Help', href: '/help', icon: HelpCircle },
];

// Widths of the placeholder rows shown while the plan is unknown — roughly the
// shape of the full menu, so nothing shifts when the real items replace them.
const NAV_SKELETON_ROWS = ['w-14', 'w-20', 'w-24', 'w-16', 'w-16', 'w-16'];

function NavItem({ item, pathname }) {
  const isActive = pathname === item.href || (item.matchPrefix && pathname.startsWith(`${item.href}/`));
  return (
    <Link
      to={item.href}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <item.icon className="w-5 h-5" />
      {item.name}
    </Link>
  );
}

export default function Layout() {
  const location = useLocation();
  // Every page change starts at the top. The pages share one scrolling window,
  // so without this the next page opened wherever the last one was scrolled to.
  React.useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') window.scrollTo(0, 0);
  }, [location.pathname]);
  const navigate = useNavigate();
  
  // Get subscription status for showing badges. Must be called unconditionally
  // at the top level — wrapping a hook in try/catch is a Rules of Hooks
  // violation and can cause hook-ordering errors that unmount the whole tree
  // (which previously made nav appear to "freeze" after a couple of clicks).
  const subscriptionStatus = useSubscriptionStatus() || {
    isPro: false, isBasic: false, isTrial: true,
    // Fail-safe defaults: if the hook returns nothing, grant nothing.
    hasFeatureAccess: () => false, hasAnyFeatureAccess: () => false, planLabel: 'Trial',
  };
  
  const planLabel = subscriptionStatus.planLabel || 'Trial';

  // Until /subscription/info has answered, the badge would read "Trial" for every
  // merchant for a moment, then jump — so while loading the nav and badge render
  // as a skeleton. A failed load (status null after loading) shows "Unavailable",
  // never a guessed plan.
  const subscriptionLoading = subscriptionStatus.loading === true;
  const planKnown = Boolean(subscriptionStatus.status);
  const shopDomain = getShopDomain() || '';
  const isDemo = localStorage.getItem('isDemo') === 'true' || shopDomain === 'demo-shop.myshopify.com';
  
  const handleLogout = () => {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Navigate to landing
    navigate('/');
  };

  const activateDemoMode = () => {
    // Clear existing data
    localStorage.clear();
    sessionStorage.clear();
    
    // Set demo data
    const demoData = {
      'shop_domain': 'demo-shop.myshopify.com',
      'shopDomain': 'demo-shop.myshopify.com',
      'shop_id': '6851d01284e65ca16f639e90',
      'shopId': '6851d01284e65ca16f639e90',
      'shopName': 'Demo Shop',
      'isDemo': 'true',
      'token': 'demo-token',
      'onboarding_completed': 'true'
    };
    
    Object.entries(demoData).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    
    // Reload page
    window.location.href = '/home';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
<div className="fixed inset-y-0 left-0 w-52 bg-white shadow-lg">
        <div className="flex h-full flex-col">
          {/* No logo / store-name block: Shopify's admin already shows the
              app's icon and name above the iframe, so the menu starts at Home. */}
          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-2 py-4" aria-busy={subscriptionLoading && !isDemo ? 'true' : undefined}>
            {subscriptionLoading && !isDemo ? (
              <div data-testid="nav-loading" className="space-y-1 animate-pulse" aria-label="Loading menu">
                {NAV_SKELETON_ROWS.map((w, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2">
                    <div className="w-5 h-5 rounded bg-gray-200" />
                    <div className={`h-3 rounded bg-gray-200 ${w}`} />
                  </div>
                ))}
              </div>
            ) : navigation.map((item) => <NavItem key={item.name} item={item} pathname={location.pathname} />)}
          </nav>

          {/* Billing + Help — at the bottom, by design */}
          <nav className="px-2 pb-2 space-y-1 border-t pt-2" aria-label="Account">
            {footerNavigation.map((item) => <NavItem key={item.name} item={item} pathname={location.pathname} />)}
          </nav>

          {/* Subscription Status */}
          {!isDemo && (
            <div className="px-4 py-3 border-t bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Plan:</span>
                {/* Label comes from the server's plan_name, so a new tier
                    names itself. The old ternary rendered "Trial" for any plan
                    that was not literally 'pro' or 'basic' — which is what a
                    $99.99 Scale merchant saw in their sidebar. Existing plans
                    render exactly the strings they rendered before. */}
                {subscriptionLoading ? (
                  <span data-testid="plan-loading" className="inline-block h-4 w-12 rounded bg-gray-200 animate-pulse" aria-label="Loading plan" />
                ) : !planKnown ? (
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-500">Unavailable</span>
                ) : (
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  planLabel === 'Basic'
                    ? 'bg-blue-100 text-blue-700'
                    : planLabel === 'Trial'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-purple-100 text-purple-700'
                }`}>
                  {planLabel}
                </span>
                )}
              </div>
              {subscriptionStatus.isTrial && subscriptionStatus.trialDaysLeft > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  {subscriptionStatus.trialDaysLeft} days left in trial
                </p>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Main Content */}
      <div className="pl-52">
        <div className="min-h-screen">
          {/* Demo Mode Banner */}
          <DemoModeBanner />
          
          {/* Page Content — wrapped in ErrorBoundary so a render crash in
              one screen falls back to a friendly retry instead of blanking
              the whole layout (sidebar nav + outlet) into a white page. */}
          <main>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}